import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { formatMoney } from "@/shared/format";
import type { Proposal } from "../types";
import { ChannelCell } from "./ChannelCell";
import { WORKLIST_COLUMNS, etichettaTipo, formatDateIt } from "./columns";
import { DocTypeBadge } from "./DocTypeBadge";
import { ReviewReasonCell } from "./ReviewReason";
import { SdiAlertBadge } from "./SdiAlertBadge";
import { StatusBadge } from "./StatusBadge";

// I dati fiscali escono dalle STESSE funzioni delle colonne desktop: se domani
// una colonna cambia fonte (es. il fallback SDI dichiarato), la card la segue
// da sola invece di dire una cosa diversa dalla tabella.
const COLONNA = new Map(WORKLIST_COLUMNS.map((c) => [c.key, c]));
const DATI_FISCALI: Array<[key: string, label: string]> = [
  ["fonte", "Fonte"],
  ["ragione_sociale", "Ragione sociale"],
  ["piva", "P.IVA"],
  ["cf", "Codice fiscale"],
  ["sdi", "Codice SDI"],
  ["pec", "PEC"],
  ["indirizzo", "Indirizzo"],
  ["cap", "CAP"],
  ["citta", "Città"],
  ["provincia", "Provincia"],
  ["country", "Paese"],
];

function cella(key: string, r: Proposal): ReactNode {
  return COLONNA.get(key)?.render?.(r) ?? null;
}

/** Bordo e fondo dicono il termine SdI, come la riga rossa/ambra in tabella. */
function cornice(r: Proposal, selezionata: boolean): string {
  if (r.sdi_alert === "rosso") return "border-red bg-red-soft";
  if (r.sdi_alert === "giallo") return "border-amber bg-amber-soft";
  if (selezionata) return "border-accent [background:color-mix(in_oklab,#C96442_5%,#FFFFFF)]";
  return "border-line bg-card";
}

/**
 * Proposta di fatturazione sul telefono (redesign 2026-10-23, Pixel 9).
 *
 * Le 22 colonne della worklist diventano una card: in alto quello che serve
 * per decidere (canale, importo, chi, che documento, a che punto, perché è
 * ferma), sotto i tasti. I dati anagrafici stanno in «Dati fiscali», chiusi:
 * si aprono per controllare un recapito, non servono a ogni riga.
 */
export function ProposalCardMobile({
  proposal: r,
  selezionabile,
  selezionata,
  onToggle,
  azioni,
}: {
  proposal: Proposal;
  selezionabile: boolean;
  selezionata: boolean;
  onToggle: (id: number) => void;
  /** I tasti di riga, già chiusi nel fieldset del permesso dalla pagina. */
  azioni: ReactNode;
}) {
  // Stessa regola della colonna Importo: su una NC vale il rimborso.
  const importo =
    r.kind === "credit_note" ? (r.refund_amount ?? r.gross_expected) : r.gross_expected;
  const date: Array<[string, string | null]> = [
    ["Ordine", r.order_date],
    ["Fattura", r.invoice_date],
    ["Documento", r.document_date],
  ];
  const conMotivo = Boolean(r.review_reason || r.registro_stato);

  return (
    <article
      data-testid={`proposta-${r.id}`}
      className={`mb-2.5 overflow-hidden rounded-2xl border ${cornice(r, selezionata)}`}
    >
      <div className="flex flex-col gap-2.5 px-3.5 pb-3 pt-2.5">
        {/* Riga 1: selezione · canale · importo. */}
        <div className="flex items-center gap-1.5">
          {selezionabile && (
            <label className="-ml-2 grid h-11 w-11 shrink-0 cursor-pointer place-items-center">
              <input
                type="checkbox"
                checked={selezionata}
                onChange={() => onToggle(r.id)}
                aria-label={`Seleziona ${r.order_ref ?? r.customer_name}`}
                className="size-[22px] cursor-pointer accent-accent-2"
              />
            </label>
          )}
          <span className="min-w-0 text-[14px] [&_span]:text-ink">
            <ChannelCell channel={r.channel} paymentMethod={r.payment_method} />
          </span>
          <span className="ml-auto shrink-0 whitespace-nowrap font-display text-[17px] font-semibold tabular-nums tracking-[-0.02em] text-ink">
            {importo != null ? formatMoney(Number(importo)) : "—"}
          </span>
        </div>

        {/* Riga 2: ordine e cliente. */}
        <div className="flex flex-col gap-0.5">
          <div className="flex min-w-0 items-center gap-2">
            {r.order_ref ? (
              <Link
                to={`/ordini/${encodeURIComponent(r.order_ref)}`}
                className="min-w-0 truncate font-display text-[16px] font-semibold tracking-[-0.01em] text-ink underline decoration-line-2 underline-offset-4"
              >
                {r.order_ref}
              </Link>
            ) : (
              <span className="font-display text-[16px] font-semibold text-ink">Riga manuale</span>
            )}
            {r.kind === "credit_note" && (
              <span className="shrink-0 rounded-full bg-paper-2 px-2 py-0.5 text-[12px] font-semibold text-ink">
                Nota di credito
              </span>
            )}
            {r.partial_shipment && (
              <span className="shrink-0 text-[12.5px] font-semibold text-ink">
                spedizione parziale
              </span>
            )}
          </div>
          <span className="truncate text-[14.5px] text-ink">
            {r.customer_name} <span className="text-[13px]">· {etichettaTipo(r)}</span>
          </span>
        </div>

        {/* Riga 3: documento e stato. */}
        <div className="flex flex-wrap items-center gap-1.5 text-[13px] text-ink">
          <DocTypeBadge docType={r.doc_type} reviewReason={r.review_reason} />
          <StatusBadge status={r.status} />
          <SdiAlertBadge proposal={r} />
          {r.document_number && (
            <span className="inline-flex min-w-0 items-center">{cella("document_number", r)}</span>
          )}
        </div>

        {/* Perché è ferma: il chip apre la spiegazione completa. */}
        {conMotivo && (
          <div className="[&_button]:min-h-[32px] [&_button]:text-[13px]">
            <ReviewReasonCell proposal={r} />
          </div>
        )}
        {r.exclusion_reason && r.status === "esclusa" && (
          <p className="text-[13.5px] text-ink">Esclusa: {r.exclusion_reason}</p>
        )}
        {r.invoice_notes && (
          <p className="rounded-lg bg-orange-500/15 px-2.5 py-1.5 text-[13.5px] font-medium text-ink">
            Nota in fattura: {r.invoice_notes}
          </p>
        )}

        {/* Date: tre colonne fisse, sempre nello stesso posto. */}
        <dl className="grid grid-cols-3 gap-2 border-t border-line pt-2.5">
          {date.map(([label, valore]) => (
            <div key={label} className="min-w-0">
              <dt className="text-[12px] text-ink">{label}</dt>
              <dd className="text-[14px] font-semibold tabular-nums text-ink">
                {formatDateIt(valore)}
              </dd>
            </div>
          ))}
        </dl>

        <details className="group rounded-xl border border-line bg-paper/60">
          <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between px-3 text-[14px] font-semibold text-ink [&::-webkit-details-marker]:hidden">
            Dati fiscali
            <svg
              className="h-4 w-4 transition-transform duration-200 group-open:rotate-180"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </summary>
          <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1.5 px-3 pb-3 text-[13.5px]">
            {DATI_FISCALI.map(([key, label]) => (
              <div key={key} className="contents">
                <dt className="text-ink">{label}</dt>
                <dd className="min-w-0 break-words font-medium text-ink [&_.truncate]:whitespace-normal">
                  {cella(key, r)}
                </dd>
              </div>
            ))}
          </dl>
        </details>
      </div>

      {/* Tasti di riga: veri tasti da pollice invece dei link della tabella. */}
      <div className="border-t border-line bg-paper/60 px-3.5 py-2.5 [&_button]:min-h-[44px] [&_button]:rounded-xl [&_button]:border [&_button]:border-line-2 [&_button]:bg-card [&_button]:px-3.5 [&_button]:text-[14px] [&_button]:font-semibold [&_button]:no-underline [&_button.text-accent]:text-ink [&_fieldset>div]:gap-2">
        {azioni}
      </div>
    </article>
  );
}
